import { QueryClientProvider } from "@tanstack/react-query"
import { notification } from "antd"
import type { IconType } from "antd/es/notification"
import { type ReactElement, useEffect, useState } from "react"
import { BrowserRouter, Route, Routes } from "react-router"

import { ONESCHEMA_UNAVAILABLE_EVENT, queryClient } from "@/api/client"
import { ProtectedRoute } from "@/components/frame/ProtectedRoute"
import { UnavailablePage } from "@/components/global/UnavailablePage"
import { PreviewBanner } from "@/components/PreviewBanner"
import { ConfigProvider } from "@/providers/ConfigProvider"
import { identify } from "@/util/analytics"
import { lazyNamed } from "@/util/lazyNamed"
import { viewManager } from "@/util/viewManager"

const LoginPage = lazyNamed(
  () => import(/* webpackChunkName: "LoginPage" */ "@/components/login/LoginPage"),
  "LoginPage",
)
const SignupPage = lazyNamed(
  () => import(/* webpackChunkName: "SignupPage" */ "@/components/login/SignupPage"),
  "SignupPage",
)
const SelectOrgPage = lazyNamed(
  () =>
    import(/* webpackChunkName: "SelectOrgPage" */ "@/components/login/SelectOrgPage"),
  "SelectOrgPage",
)
const VerifyMagicLinkPage = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "VerifyMagicLinkPage" */ "@/components/login/VerifyMagicLinkPage"
    ),
  "VerifyMagicLinkPage",
)
const LeadDetailsPage = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "LeadDetailsPage" */ "@/components/login/LeadDetailsPage"
    ),
  "LeadDetailsPage",
)
const ImporterLauncherErrorPage = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "ImporterLauncherErrorPage" */ "@/components/importer/launcher/ImporterLauncherErrorPage"
    ),
  "ImporterLauncherErrorPage",
)
const ImporterLauncherPage = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "ImporterLauncherPage" */ "@/components/importer/launcher/ImporterLauncherPage"
    ),
  "ImporterLauncherPage",
)
const Frame = lazyNamed(
  () => import(/* webpackChunkName: "Frame" */ "@/components/frame/Frame"),
  "Frame",
)
const FileFeedsEmbedTransformsLauncher = lazyNamed(
  () =>
    import(
      /* webpackChunkName: "FileFeedsEmbedTransformsLauncher" */ "@/components/fileFeeds/embedded/FileFeedsEmbedTransformsLauncher"
    ),
  "FileFeedsEmbedTransformsLauncher",
)

const openNotificationWithIcon = (type: IconType, message: string) => {
  notification[type]({ message })
}

// Supports routing between the main frame and various widget pages
export function BaseRouter(): ReactElement | null {
  const org = viewManager.get("org")
  const user = viewManager.get("user")
  const [unavailable, setUnavailable] = useState(false)
  useEffect(
    () => {
      const flash = viewManager.get("flash")
      if (flash) {
        openNotificationWithIcon(flash.type, flash.text)
      }

      if (org && user) {
        identify(org, user)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  useEffect(() => {
    const onUnavailable = () => setUnavailable(true)
    window.addEventListener(ONESCHEMA_UNAVAILABLE_EVENT, onUnavailable)
    return () => window.removeEventListener(ONESCHEMA_UNAVAILABLE_EVENT, onUnavailable)
  }, [])

  if (unavailable) {
    return <UnavailablePage />
  }

  // If you add a route here, you'll have to manually add a
  // handler in controllers/main.rb.
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PreviewBanner />
        <Routes>
          <Route path="/login/*" element={<LoginPage />} />
          <Route path="/signup/*" element={<SignupPage />} />
          <Route
            path="/introduce-yourself/*"
            element={<LeadDetailsPage user={user} org={org} />}
          />
          <Route path="/select-organization/*" element={<SelectOrgPage />} />
          <Route path="/verify-magic-link/*" element={<VerifyMagicLinkPage />} />
          <Route path="/embed-launcher/*" element={<ImporterLauncherPage />} />
          {/**
           * customizations won't exist for this page because there is no org
           * but components can rely on having the provider available.
           * Eventually this might/should have the same entry point as `/embed-launcher`
           * and that place should do the provider wrapping for both instead
           *  */}
          <Route
            path="/embed-launcher-error/*"
            element={
              <ConfigProvider>
                <ImporterLauncherErrorPage />
              </ConfigProvider>
            }
          />
          <Route
            path="/file-feeds-embed/launcher/*"
            element={<FileFeedsEmbedTransformsLauncher />}
          />
          <Route path="*" element={<ProtectedRoute />}>
            <Route path="*" element={<Frame />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
